import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { PhotoCamera as PhotoIcon } from "@mui/icons-material";
import ContentLayout from "../../layouts/ContentLayout";
import { useAuth } from "../../contexts/AuthContext";
import { userService, buildFileUrl } from "../../services/api";

function Profile() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    walletAddress: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    twoFactorAuth: false,
    darkMode: false,
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await userService.getProfile();
        if (response.data && response.data.user) {
          const userData = response.data.user;
          // Actualizar el usuario en el contexto si hay datos nuevos (especialmente la foto)
          if (user && userData.photo !== user.photo) {
            updateUser({ ...user, ...userData });
          }
          setFormData((prev) => ({
            ...prev,
            name: userData.name || "",
            email: userData.email || "",
            walletAddress: userData.walletAddress || "",
          }));
        } else if (user) {
          setFormData((prev) => ({
            ...prev,
            name: user.name || "",
            email: user.email || "",
            walletAddress: user.walletAddress || "",
          }));
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        if (user) {
          setFormData((prev) => ({
            ...prev,
            name: user.name || "",
            email: user.email || "",
            walletAddress: user.walletAddress || "",
          }));
        }
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSettingChange = (name) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [name]: event.target.checked,
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const response = await userService.updateProfile({
        name: formData.name,
        walletAddress: formData.walletAddress,
      });

      if (response.data && response.data.user) {
        updateUser(response.data.user);
      }
      setSuccess("Perfil actualizado exitosamente");
    } catch (error) {
      setError("Error al actualizar el perfil");
      console.error("Error al actualizar perfil:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (formData.newPassword !== formData.confirmPassword) {
        setError("Las contraseñas no coinciden");
        return;
      }

      await userService.updatePassword(user.id, {
        newPassword: formData.newPassword,
      });

      setSuccess("Contraseña actualizada exitosamente");
      setFormData((prev) => ({
        ...prev,
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (error) {
      setError("Error al actualizar la contraseña");
      console.error("Error al actualizar contraseña:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const formDataToSend = new FormData();
      formDataToSend.append("photo", file);
      const response = await userService.updatePhoto(formDataToSend);
      if (response.data && response.data.user) {
        updateUser(response.data.user);
      }
      setSuccess("Foto de perfil actualizada exitosamente");
    } catch (error) {
      setError("Error al actualizar la foto de perfil");
      console.error("Error al actualizar foto:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <ContentLayout title="Perfil">
        <CircularProgress />
      </ContentLayout>
    );
  }

  return (
    <ContentLayout title="Perfil">
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Avatar
                src={user.photo ? buildFileUrl(user.photo, "profiles") : null}
                alt={user.name}
                sx={{ width: 120, height: 120, mb: 2 }}
                onError={(e) => {
                  console.error("❌ Error loading profile photo:", {
                    userPhoto: user.photo,
                    photoUrl: buildFileUrl(user.photo, "profiles"),
                    error: e,
                  });
                  e.target.style.display = "none";
                }}
                onLoad={() => {
                  console.log("✅ Profile photo loaded:", {
                    userPhoto: user.photo,
                    photoUrl: buildFileUrl(user.photo, "profiles"),
                  });
                }}
              >
                {!user.photo && user.name.charAt(0).toUpperCase()}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="photo-upload"
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="photo-upload">
                <Button
                  component="span"
                  variant="contained"
                  startIcon={<PhotoIcon />}
                  sx={{ position: "absolute", bottom: 0, right: 0 }}
                >
                  Cambiar
                </Button>
              </label>
            </Box>
            <Typography variant="h6">{user.name}</Typography>
            <Typography color="text.secondary">{user.email}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 3 }}>
              Información Personal
            </Typography>
            <Box component="form" onSubmit={handleProfileUpdate}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    name="email"
                    value={formData.email}
                    disabled
                    helperText="El email no se puede modificar"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección de Wallet (Opcional)"
                    name="walletAddress"
                    value={formData.walletAddress}
                    onChange={handleInputChange}
                    placeholder="0x..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Guardar Cambios"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3 }}>
              Cambiar Contraseña
            </Typography>
            <Box component="form" onSubmit={handlePasswordUpdate}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Nueva contraseña"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirmar nueva contraseña"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Cambiar Contraseña"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />
          </Paper>
        </Grid>
      </Grid>
    </ContentLayout>
  );
}

export default Profile;
